import { ReactNode, useState, MouseEvent } from 'react';
import {
  Button,
  ButtonBase,
  Box,
  Grow,
  Paper,
  Popper,
  ButtonProps,
  PopperProps,
  ClickAwayListener,
  SxProps,
  Theme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { DropdownOption } from '../types';

export interface DropdownProps extends ButtonProps {
  readonly label: ReactNode;
  readonly options?: DropdownOption[];
  readonly arrow?: boolean;
  readonly popperWidth?: number | 'auto';
  readonly popperPlacement?: PopperProps['placement'];
  // SAA-112 design pass — everything below is optional and defaults to today's behaviour, so
  // `ExportPdfDropdown`/`QuestionnaireExportPdfDropdown` render byte-identical.
  /** A header rendered above the option list (e.g. "Applications"). */
  readonly header?: ReactNode;
  /** Merged onto each option's `ButtonBase`, after the built-in row styling. */
  readonly itemSx?: SxProps<Theme>;
  /** Merged onto the list `Paper`, after the built-in `py/my`. */
  readonly paperSx?: SxProps<Theme>;
  /** Merged onto the trigger `Button` ONLY while open — for a design's "pressed" bg. */
  readonly activeSx?: SxProps<Theme>;
  /** Gap between the trigger and the popper, in px (Popper's `offset` modifier). Default 0 (today's behaviour). */
  readonly popperOffset?: number;
  /** Renders a custom trigger instead of the default `Button` — the wrapping element becomes the anchor. */
  readonly renderTrigger?: (params: { open: boolean }) => ReactNode;
  /** Max height of the option list before it scrolls. `'none'` lets it grow freely. Default 200 (today's behaviour). */
  readonly listMaxHeight?: number | 'none';
}

export const Dropdown = ({
  options,
  label,
  children,
  arrow,
  color = 'primary',
  variant = 'contained',
  sx,
  popperWidth = 190,
  popperPlacement = 'bottom-end',
  className,
  header,
  itemSx,
  paperSx,
  activeSx,
  popperOffset = 0,
  renderTrigger,
  listMaxHeight = 200,
}: DropdownProps) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleToggle = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClose} mouseEvent="onMouseUp">
      <span>
        {renderTrigger ? (
          <Box onClick={handleToggle} sx={{ display: 'inline-flex' }}>
            {renderTrigger({ open })}
          </Box>
        ) : (
          <Button
            className={className}
            sx={[
              {
                minWidth: 0,
              },
              ...(Array.isArray(sx) ? sx : [sx]),
              ...(open && activeSx ? (Array.isArray(activeSx) ? activeSx : [activeSx]) : []),
            ]}
            onClick={handleToggle}
            color={color}
            variant={variant}
            endIcon={
              arrow && (
                <KeyboardArrowDownIcon
                  sx={{
                    color: 'inherit',
                    transform: open ? 'rotate(180deg)' : '',
                  }}
                />
              )
            }
          >
            {label}
          </Button>
        )}

        <Popper
          open={open}
          anchorEl={anchorEl}
          placement={popperPlacement}
          transition
          style={{
            width: popperWidth,
          }}
          modifiers={[{ name: 'offset', options: { offset: [0, popperOffset] } }]}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} timeout={400}>
              <Paper sx={[{ py: 0.5, my: 0.5 }, ...(Array.isArray(paperSx) ? paperSx : [paperSx])]}>
                {header}
                <Box
                  className="styled-scrollbar"
                  sx={{
                    py: options ? '' : 1,
                    px: options ? '' : 2,
                    ...(listMaxHeight === 'none' ? {} : { maxHeight: listMaxHeight }),
                  }}
                >
                  {options ? (
                    options.map(option => (
                      <ButtonBase
                        key={option.id}
                        sx={[
                          {
                            justifyContent: 'flex-start',
                            py: 1,
                            px: 2,
                            my: 0.25,
                            width: '100%',

                            '&:hover': {
                              bgcolor: 'grey.200',
                            },
                          },
                          ...(Array.isArray(itemSx) ? itemSx : [itemSx]),
                        ]}
                        onClick={() => {
                          if (option.onClick) {
                            option.onClick();
                          }
                          handleClose();
                        }}
                      >
                        {option.name}
                      </ButtonBase>
                    ))
                  ) : (
                    <>{children}</>
                  )}
                </Box>
              </Paper>
            </Grow>
          )}
        </Popper>
      </span>
    </ClickAwayListener>
  );
};
